/**
 * Boat 360 Plus Subscription Modal
 *
 * Android: uses @capgo/native-purchases (Google Play Billing).
 * iOS:     uses @capgo/native-purchases (StoreKit).
 * Web:     shows store download links.
 *
 * APPLE / GOOGLE COMPLIANCE: subscription name, price, duration, auto-renewal
 * disclosure, clickable Privacy Policy + Terms links are all displayed below.
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Crown,
  Lock,
  Users,
  Bell,
  Share2,
  Navigation,
  FileText,
  Route,
  Volume2,
  X,
  Check,
  ExternalLink,
  Loader2,
  AlertCircle,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { NativePurchases, PURCHASE_TYPE } from '@capgo/native-purchases';

const PREMIUM_FEATURES = [
  { icon: Route, label: 'Auto-Route Around Shallows', description: 'Charts a safe path avoiding shallow water' },
  { icon: Volume2, label: 'Voice Turn-by-Turn Guidance', description: 'Spoken directions leg-by-leg as you go' },
  { icon: Users, label: 'Add Friends & Family', description: 'Track your crew in real-time' },
  { icon: Lock, label: 'Private SOS', description: 'Alert friends with your exact location' },
  { icon: Navigation, label: 'Background Tracking', description: 'Stay visible during trips' },
  { icon: FileText, label: 'Float Plans', description: 'Create & share trip plans' },
  { icon: Share2, label: 'Trip Sharing Links', description: 'Share your trip with anyone' },
  { icon: Bell, label: 'Overdue Alerts', description: 'Auto-notify if you\'re late' },
];

const FREE_FEATURES = [
  'View public boaters & hazards',
  'Shallow-water depth warnings on your route',
  'Report hazards & marine activity',
  'Public anonymous SOS alerts',
  'All map layers & styles',
];

// Product IDs - MUST match App Store Connect / Google Play Console EXACTLY
const IOS_PRODUCT_ID = 'com.safetymindai.boat360.plus.annual';
const ANDROID_PRODUCT_ID = 'boat360_plus_yearly';
const ANDROID_BASE_PLAN_ID = 'yearly'; // Google Play base plan id (required for subscriptions)

const PremiumUpgradeModal = ({ isOpen, onClose, onUpgrade, blockedFeature = null }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [isNativeApp, setIsNativeApp] = useState(false);
  const [platform, setPlatform] = useState('web');
  const [storeProduct, setStoreProduct] = useState(null);

  // Detect platform on mount and initialize the store
  useEffect(() => {
    const native = window.Capacitor?.isNativePlatform?.() || false;
    const plat = window.Capacitor?.getPlatform?.() || 'web';
    setIsNativeApp(native);
    setPlatform(plat);

    console.log('[IAP] Platform:', { native, platform: plat });

    if (native && plat === 'android') {
      initAndroidStore();
    } else if (native && plat === 'ios') {
      initIosStore();
    }
  }, []);

  // ============================================
  // ANDROID — @capgo/native-purchases (Google Play Billing)
  // ============================================
  const initAndroidStore = async () => {
    try {
      const { isBillingSupported } = await NativePurchases.isBillingSupported();
      if (!isBillingSupported) {
        setPurchaseError('Google Play billing is not available on this device.');
        return;
      }
      const { products } = await NativePurchases.getProducts({
        productIdentifiers: [ANDROID_PRODUCT_ID],
        productType: PURCHASE_TYPE.SUBS,
      });
      console.log('[IAP][Android] products:', products);
      if (products && products.length > 0) {
        setStoreProduct(products[0]);
      } else {
        console.warn('[IAP][Android] No products returned for', ANDROID_PRODUCT_ID);
      }
    } catch (error) {
      console.error('[IAP][Android] init error:', error);
    }
  };

  const handleAndroidSubscribe = async () => {
    setIsLoading(true);
    setPurchaseError('');
    try {
      // NOTE: @capgo/native-purchases quirk — for Android SUBS, purchaseProduct
      // queries Google Play using `planIdentifier` as the *product id* (see the
      // plugin's setProductId), then auto-selects the matching base plan (or the
      // first one). So `planIdentifier` MUST be the subscription product id, NOT
      // the base plan id, otherwise Google returns "Product not found".
      const transaction = await NativePurchases.purchaseProduct({
        productIdentifier: ANDROID_PRODUCT_ID,
        planIdentifier: ANDROID_PRODUCT_ID,
        productType: PURCHASE_TYPE.SUBS,
      });

      console.log('[IAP][Android] transaction:', transaction);

      if (transaction && transaction.transactionId) {
        await reportPurchase('android', ANDROID_PRODUCT_ID, transaction.transactionId);
      } else {
        setPurchaseError('Purchase was not completed. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('[IAP][Android] purchase error:', error);
      const msg = (error?.message || '').toLowerCase();
      if (msg.includes('cancel')) {
        // User cancelled — no error message
      } else {
        setPurchaseError(`Unable to complete purchase: ${error?.message || 'Please try again.'}`);
      }
      setIsLoading(false);
    }
  };

  const handleAndroidRestore = async () => {
    setIsRestoring(true);
    setPurchaseError('');
    try {
      const result = await NativePurchases.restorePurchases();
      const info = result?.customerInfo;
      const active = info?.activeSubscriptions || [];
      const owned = info?.allPurchasedProductIdentifiers || [];
      if (active.includes(ANDROID_PRODUCT_ID) || owned.includes(ANDROID_PRODUCT_ID)) {
        await reportPurchase('android', ANDROID_PRODUCT_ID, `restored-${Date.now()}`);
      } else {
        toast.info('No active Boat 360 Plus subscription found on this Google account.');
        setIsRestoring(false);
      }
    } catch (error) {
      console.error('[IAP][Android] restore error:', error);
      setPurchaseError('Could not restore purchases. Please try again.');
      setIsRestoring(false);
    }
  };

  // ============================================
  // iOS — @capgo/native-purchases (StoreKit)
  // ============================================
  const initIosStore = async () => {
    try {
      const { isBillingSupported } = await NativePurchases.isBillingSupported();
      if (!isBillingSupported) {
        setPurchaseError('In-app purchases are not available on this device.');
        return;
      }
      const { products } = await NativePurchases.getProducts({
        productIdentifiers: [IOS_PRODUCT_ID],
      });
      console.log('[IAP][iOS] products:', products);
      if (products && products.length > 0) setStoreProduct(products[0]);
    } catch (error) {
      console.error('[IAP][iOS] init error:', error);
    }
  };

  const handleIosSubscribe = async () => {
    setIsLoading(true);
    setPurchaseError('');
    try {
      const transaction = await NativePurchases.purchaseProduct({
        productIdentifier: IOS_PRODUCT_ID,
        quantity: 1,
      });
      console.log('[IAP][iOS] transaction:', transaction);
      if (transaction && transaction.transactionId) {
        await reportPurchase('ios', IOS_PRODUCT_ID, transaction.transactionId);
      } else {
        setPurchaseError('Purchase was not completed. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('[IAP][iOS] purchase error:', error);
      const msg = (error?.message || '').toLowerCase();
      if (msg.includes('cancel')) {
        // User cancelled — no error message
      } else {
        setPurchaseError(`Unable to complete purchase: ${error?.message || 'Please try again.'}`);
      }
      setIsLoading(false);
    }
  };

  const handleIosRestore = async () => {
    setIsRestoring(true);
    setPurchaseError('');
    try {
      const result = await NativePurchases.restorePurchases();
      const info = result?.customerInfo;
      const active = info?.activeSubscriptions || [];
      const owned = info?.allPurchasedProductIdentifiers || [];
      if (active.includes(IOS_PRODUCT_ID) || owned.includes(IOS_PRODUCT_ID)) {
        await reportPurchase('ios', IOS_PRODUCT_ID, `restored-${Date.now()}`);
      } else {
        toast.info('No active Boat 360 Plus subscription found on this Apple ID.');
        setIsRestoring(false);
      }
    } catch (error) {
      console.error('[IAP][iOS] restore error:', error);
      setPurchaseError('Could not restore purchases. Please try again.');
      setIsRestoring(false);
    }
  };

  // ============================================
  // SHARED — report successful purchase to backend
  // ============================================
  const reportPurchase = async (plat, productId, purchaseToken, orderId) => {
    try {
      await api.post('/subscription/verify', {
        platform: plat,
        product_id: productId,
        purchase_token: purchaseToken,
        order_id: orderId,
      });
      toast.success('🎉 Welcome to Boat 360 Plus!');
      if (onUpgrade) onUpgrade();
      onClose();
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      console.error('[IAP] backend verify failed:', e);
      setPurchaseError('Payment succeeded but activation failed. Tap "Restore Purchases" or contact support.');
      setIsLoading(false);
      setIsRestoring(false);
    }
  };

  // Dispatcher
  const handleSubscribe = () => {
    setPurchaseError('');
    if (!isNativeApp) {
      setPurchaseError('Subscriptions are available in the Boat 360 mobile app. Download from the App Store or Google Play.');
      return;
    }
    if (platform === 'android') return handleAndroidSubscribe();
    return handleIosSubscribe();
  };

  const handleRestore = () => {
    if (platform === 'android') return handleAndroidRestore();
    return handleIosRestore();
  };

  if (!isOpen) return null;

  const getTermsUrl = () => {
    if (platform === 'ios') {
      return 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/';
    }
    return 'https://marine-tracker-8.emergent.host/terms';
  };

  const priceDisplay = storeProduct?.priceString || '$19.99';

  return (
    <div className="fixed inset-0 bg-black/60 z-[2000] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardContent className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">Boat 360 Plus</h2>
                <p className="text-sm text-gray-500">Auto-Renewable Subscription</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full"
              data-testid="close-upgrade-modal"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Blocked Feature Notice */}
          {blockedFeature && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <Lock className="w-4 h-4 inline mr-1" />
                <strong>{blockedFeature}</strong> requires Boat 360 Plus
              </p>
            </div>
          )}

          {/* Premium Features List */}
          <div className="space-y-3 mb-6">
            {PREMIUM_FEATURES.map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-4 h-4 text-cyan-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-sm">{feature.label}</p>
                  <p className="text-xs text-gray-500">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* SUBSCRIPTION DETAILS - REQUIRED BY APPLE/GOOGLE */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg p-4 text-white text-center mb-4">
            <p className="text-cyan-100 text-sm mb-1">Boat 360 Plus</p>
            <p className="font-bold text-3xl">{priceDisplay}</p>
            <p className="text-cyan-100">per year</p>
          </div>

          {/* Subscribe Button */}
          <Button
            onClick={handleSubscribe}
            disabled={isLoading || isRestoring}
            className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 text-lg mb-3"
            data-testid="subscribe-now-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Crown className="w-5 h-5 mr-2" />
                Subscribe Now
              </>
            )}
          </Button>

          {/* Restore Purchases — native only */}
          {isNativeApp && (
            <Button
              onClick={handleRestore}
              disabled={isLoading || isRestoring}
              variant="outline"
              className="w-full mb-4"
              data-testid="restore-purchases-btn"
            >
              {isRestoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Restore Purchases
                </>
              )}
            </Button>
          )}

          {/* Error Message */}
          {purchaseError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2" data-testid="purchase-error">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{purchaseError}</p>
            </div>
          )}

          {/* LEGAL DISCLOSURES - REQUIRED BY APPLE 3.1.2 */}
          <div className="border-t pt-4 space-y-3 text-xs text-gray-600">
            <p className="text-center">
              Subscription automatically renews unless canceled at least 24 hours before the end of the current period.
              Your {platform === 'ios' ? 'Apple ID' : platform === 'android' ? 'Google Play' : 'app store'} account will be charged $19.99 for renewal within 24 hours prior to the end of the current period.
            </p>
            <p className="text-center">
              You can manage or cancel your subscription anytime in your {platform === 'ios' ? 'Apple ID Account Settings' : platform === 'android' ? 'Google Play subscription settings' : 'device account settings'}.
            </p>
            <div className="flex justify-center gap-6 pt-2">
              <a
                href="https://marine-tracker-8.emergent.host/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-600 hover:text-cyan-800 underline flex items-center gap-1 font-medium"
                data-testid="privacy-policy-link"
              >
                Privacy Policy
                <ExternalLink className="w-3 h-3" />
              </a>
              <a
                href={getTermsUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-600 hover:text-cyan-800 underline flex items-center gap-1 font-medium"
                data-testid="terms-of-use-link"
              >
                Terms of Use
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Web Users - Download App */}
          {!isNativeApp && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-gray-600 text-center mb-3">
                Download the app to subscribe:
              </p>
              <div className="flex gap-2">
                <a
                  href="https://apps.apple.com/app/boat-360/id6740043541"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-black text-white text-center py-2 px-3 rounded-lg text-sm font-medium hover:bg-gray-800 flex items-center justify-center gap-1"
                >
                  App Store <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.boat360.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-green-600 text-white text-center py-2 px-3 rounded-lg text-sm font-medium hover:bg-green-700 flex items-center justify-center gap-1"
                >
                  Google Play <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          )}

          {/* Free Features */}
          <div className="mt-4 pt-4 border-t">
            <p className="text-xs text-gray-500 text-center mb-2">Already included free:</p>
            <div className="flex flex-wrap justify-center gap-1">
              {FREE_FEATURES.map((feature, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  <Check className="w-3 h-3 mr-1" />
                  {feature}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Compact badge for showing in menu/header
export const PremiumBadge = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-3 py-1.5 rounded-full text-sm font-medium shadow-lg hover:shadow-xl transition-all"
    data-testid="premium-badge"
  >
    <Crown className="w-4 h-4" />
    <span>Get Plus</span>
  </button>
);

export default PremiumUpgradeModal;
